// WebUSB 直连热敏打印机（ESC/POS）
// 仅支持 Chrome / Edge（需要 HTTPS 环境）
//
// 打印机类代码 classCode = 7 (USB 打印机类)
// 得力 5801PW 等 ESC/POS 热敏打印机均属于此类

let device = null; // 模块级单例，页面刷新后需重连或从 getDevices 恢复

/**
 * 当前浏览器是否支持 WebUSB
 */
export function isWebUSPSupported() {
  return typeof navigator !== 'undefined' && 'usb' in navigator;
}

/**
 * 获取已授权的打印机（页面刷新后授权仍有效）
 * @returns {USBDevice|null}
 */
export async function getConnectedPrinter() {
  if (!isWebUSPSupported()) return null;
  const devices = await navigator.usb.getDevices();
  return devices[0] || null;
}

/**
 * 弹出浏览器设备选择框，让用户选择并授权打印机
 * 用户取消选择会抛出 NotFoundError
 */
export async function requestPrinter() {
  if (!isWebUSPSupported()) {
    throw new Error('当前浏览器不支持 WebUSB，请使用 Chrome 或 Edge 浏览器');
  }
  return navigator.usb.requestDevice({
    filters: [{ classCode: 7 }] // USB 打印机类
  });
}

/**
 * 打开并认领打印机接口
 * 内部幂等：已打开则跳过
 */
export async function openPrinter(dev) {
  device = dev;
  if (!device.opened) {
    await device.open();
  }
  if (device.configuration === null) {
    await device.selectConfiguration(1);
  }
  // 找到打印机类接口并认领
  const interfaces = device.configuration.interfaces || [];
  for (const iface of interfaces) {
    const alt = iface.alternates && iface.alternates[0];
    if (alt && alt.interfaceClass === 7) { // 打印机类
      if (!iface.claimed) {
        await device.claimInterface(iface.interfaceNumber);
      }
      break;
    }
  }
  return device;
}

/**
 * 发送打印数据
 * @param {Uint8Array} data ESC/POS 字节流
 */
export async function printBytes(data) {
  if (!device) throw new Error('打印机未连接，请先连接打印机');

  // 找出方向的 bulk endpoint 号
  let endpointNumber = 1; // 兜底
  const interfaces = (device.configuration && device.configuration.interfaces) || [];
  for (const iface of interfaces) {
    for (const alt of iface.alternates || []) {
      const ep = (alt.endpoints || []).find(e => e.direction === 'out');
      if (ep) { endpointNumber = ep.endpointNumber; break; }
    }
  }

  try {
    await device.transferOut(endpointNumber, data);
  } catch (e) {
    // 传输失败大概率是设备掉线，清空单例让用户重连
    device = null;
    throw new Error('发送打印数据失败，请重新连接打印机（' + e.message + '）');
  }
}

/**
 * 一键打印流程：获取已授权设备 → 没有则弹框选择 → 打开 → 打印
 * @param {Uint8Array} data
 */
export async function printToThermalPrinter(data) {
  let dev = await getConnectedPrinter();
  if (!dev) {
    dev = await requestPrinter();
  }
  await openPrinter(dev);
  await printBytes(data);
  return dev;
}